class TabsComponent extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, tab));
    });
  }

  select(tab, focus = false) {
    this.tabs.forEach((item) => {
      const selected = item === tab;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(item.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });
    if (focus) tab.focus();
  }

  onKeydown(event, tab) {
    const index = this.tabs.indexOf(tab);
    const last = this.tabs.length - 1;
    const next = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    this.select(this.tabs[next], true);
  }
}
customElements.define('tabs-component', TabsComponent);

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      const input = event.target;
      if (input.name !== 'id') return;
      const scope = this.closest('[data-product-scope]') || document;
      const image = scope.querySelector('[data-variant-image]');
      if (image && input.dataset.imageSrc) {
        image.src = input.dataset.imageSrc;
        image.removeAttribute('srcset');
        image.alt = input.dataset.imageAlt || image.alt;
      }
      const price = scope.querySelector('[data-price-current]');
      if (price && input.dataset.price) price.textContent = input.dataset.price;
      const compare = scope.querySelector('[data-price-compare]');
      if (compare) {
        compare.textContent = input.dataset.compare || '';
        compare.hidden = !input.dataset.compare;
      }
      scope.querySelectorAll('[data-add-to-cart]').forEach((button) => {
        button.disabled = input.dataset.available !== 'true';
      });
      scope.querySelectorAll('input[name="id"]').forEach((other) => {
        if (other !== input && other.value === input.value) other.checked = true;
      });
    });
  }
}
customElements.define('variant-picker', VariantPicker);

// Cart page: quantity changes and removals are saved through the Cart AJAX API, and the cart section
// and header cart icon are re-rendered from the same response (bundled section rendering).
// Without JavaScript the form still works through its "Update cart" button.
let cartQueue = Promise.resolve();

class CartItems extends HTMLElement {
  connectedCallback() {
    this.addEventListener('input', (event) => {
      if (!event.target.matches('input[data-key]')) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.onQuantity(event.target), 500);
    });
    this.addEventListener('change', (event) => {
      if (!event.target.matches('input[data-key]')) return;
      clearTimeout(this.timer);
      this.onQuantity(event.target);
    });
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('a[data-key]');
      if (!remove) return;
      event.preventDefault();
      this.update(remove.dataset.key, 0);
    });
  }

  onQuantity(input) {
    if (input.value === '') return;
    const max = Number(input.max) || Infinity;
    const quantity = Math.min(max, Math.max(0, parseInt(input.value, 10) || 0));
    input.value = quantity;
    if (String(quantity) === input.dataset.quantity) return;
    this.update(input.dataset.key, quantity, input.id);
  }

  // Requests run one at a time; a queued request runs on the re-rendered element if this one was replaced.
  update(key, quantity, inputId) {
    cartQueue = cartQueue.then(() => (this.isConnected ? this : document.querySelector('cart-items'))?.send(key, quantity, inputId));
  }

  async send(key, quantity, inputId) {
    const sectionId = this.dataset.sectionId;
    const header = document.querySelector('.site-header')?.closest('.shopify-section');
    const sections = [sectionId];
    if (header) sections.push(header.id.replace('shopify-section-', ''));
    const error = this.querySelector('[data-cart-error]');
    error.hidden = true;
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections, sections_url: location.pathname }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || '');
      this.render(sectionId, data.sections, header);
    } catch (failure) {
      const input = inputId && document.getElementById(inputId);
      if (input) input.value = input.dataset.quantity;
      error.textContent = failure.message || this.dataset.errorText;
      error.hidden = false;
      this.removeAttribute('aria-busy');
    }
  }

  render(sectionId, sections, header) {
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
    const current = document.getElementById(`shopify-section-${sectionId}`);
    const fresh = parse(sections[sectionId]).getElementById(`shopify-section-${sectionId}`);
    if (!current || !fresh) return location.reload();
    const focusedId = document.activeElement?.id;
    const note = current.querySelector('[name="note"]')?.value;
    const updatedText = this.dataset.updatedText;
    current.innerHTML = fresh.innerHTML;

    const noteField = current.querySelector('[name="note"]');
    if (noteField && note !== undefined) noteField.value = note;
    const focusTarget = (focusedId && document.getElementById(focusedId)) || current.querySelector('h1');
    if (document.activeElement === document.body || !document.activeElement) focusTarget?.focus();

    if (header && sections[header.id.replace('shopify-section-', '')]) {
      const freshIcon = parse(sections[header.id.replace('shopify-section-', '')]).querySelector('.site-header__cart');
      const icon = header.querySelector('.site-header__cart');
      if (freshIcon && icon) icon.replaceWith(freshIcon);
    }

    const subtotal = current.querySelector('[data-cart-subtotal]');
    announce(subtotal ? updatedText.replace('[subtotal]', subtotal.textContent.trim()) : current.querySelector('.cart__empty')?.textContent || '');
  }
}
customElements.define('cart-items', CartItems);

function announce(message) {
  let region = document.getElementById('LiveStatus');
  if (!region) {
    region = Object.assign(document.createElement('div'), { id: 'LiveStatus', className: 'visually-hidden' });
    region.setAttribute('role', 'status');
    document.body.append(region);
  }
  region.textContent = '';
  setTimeout(() => (region.textContent = message), 100);
}

const leadDialog = () => document.getElementById('LeadFormDialog');

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-lead-form]');
  if (opener) {
    const dialog = leadDialog();
    if (!dialog) return;
    event.preventDefault();
    const title = opener.dataset.leadTitle;
    if (title) dialog.querySelector('[data-lead-title]').textContent = title;
    dialog.querySelector('[data-lead-request]').value = opener.dataset.leadForm;
    dialog.showModal();
    return;
  }

  const closer = event.target.closest('[data-dialog-close]');
  if (closer) closer.closest('dialog').close();
});

document.addEventListener('DOMContentLoaded', () => {
  const dialog = leadDialog();
  if (dialog && dialog.querySelector('[role="alert"]:not([hidden])')) dialog.showModal();
});

// Fade-in-up on scroll for [data-reveal] elements (the original site animates content the same way).
if ('IntersectionObserver' in window && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        revealObserver.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px' }
  );
  document.documentElement.classList.add('reveal-ready');
  const observeReveal = (root) => root.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => revealObserver.observe(el));
  observeReveal(document);
  // Sections re-rendered in the theme editor must be observed again.
  document.addEventListener('shopify:section:load', (event) => observeReveal(event.target));
}

// Smooth open/close for FAQ <details> elements.
document.addEventListener('click', (event) => {
  const summary = event.target.closest('.faq-item > summary');
  if (!summary || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const details = summary.parentElement;
  const answer = details.querySelector('.faq-item__answer');
  event.preventDefault();
  // Start from the current height so a click during an animation reverses it without a jump.
  const from = details.open ? answer.getBoundingClientRect().height : 0;
  answer.getAnimations().forEach((running) => running.cancel());
  const opening = !details.open || details.dataset.closing === 'true';
  delete details.dataset.closing;
  details.open = true;
  const to = opening ? answer.scrollHeight : 0;
  if (!opening) details.dataset.closing = 'true';
  answer.style.overflow = 'hidden';
  const animation = answer.animate(
    { height: [`${from}px`, `${to}px`], opacity: opening ? [0, 1] : [1, 0] },
    { duration: 300, easing: 'ease-in-out' }
  );
  animation.onfinish = () => {
    answer.style.overflow = '';
    if (details.dataset.closing === 'true') {
      details.open = false;
      delete details.dataset.closing;
    }
  };
});

// Lead form posting to an external endpoint (Telegram worker): JSON request, then the success page.
document.addEventListener('submit', async (event) => {
  const form = event.target.closest('form[data-lead-endpoint]');
  if (!form) return;
  event.preventDefault();
  const button = form.querySelector('[type="submit"]');
  const error = form.querySelector('[data-lead-error]');
  const field = (name) => form.elements[name]?.value ?? '';
  error.hidden = true;
  button.disabled = true;
  try {
    const response = await fetch(form.dataset.leadEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: field('contact[name]'),
        phone: field('contact[phone]'),
        request: field('contact[request]'),
        website: field('website'),
        page: location.href,
      }),
    });
    if (response.ok) {
      location.href = form.dataset.successUrl;
      return;
    }
    error.textContent = response.status === 429 ? form.dataset.rateText : form.dataset.errorText;
  } catch {
    error.textContent = form.dataset.errorText;
  }
  error.hidden = false;
  button.disabled = false;
});
